import { useEffect } from "react";

/**
 * Per-route `<title>` and description, replacing Next's `generateMetadata`.
 *
 * The server shell already writes both into the HTML before it leaves the
 * process, which is what a crawler and a link preview read — this hook is for
 * what happens *after* that: a client-side navigation changes the route without
 * a new document, so the tab title would otherwise keep naming the page the
 * visitor arrived on.
 *
 * The title template matches the one the layout used to apply: a page states
 * its own name and the suffix is added here, so no call site has to remember
 * it.
 */
const SUFFIX = "AsetQu";
const DEFAULT_TITLE = "AsetQu · Official Migration Portal";

export function pageTitle(title?: string): string {
  return title ? `${title} · ${SUFFIX}` : DEFAULT_TITLE;
}

export function usePageMeta(meta: { title?: string; description?: string }): void {
  const { title, description } = meta;

  useEffect(() => {
    document.title = pageTitle(title);

    if (description === undefined) return;
    setMeta("name", "description", description);
    setMeta("property", "og:description", description);
  }, [title, description]);

  useEffect(() => {
    if (title === undefined) return;
    setMeta("property", "og:title", pageTitle(title));
  }, [title]);
}

/**
 * `name` for the standard description, `property` for Open Graph.
 *
 * The two spellings are not interchangeable: a description written under
 * `property` is invisible to search engines, and an `og:` tag under `name` is
 * invisible to every social preview. The server writes them the same way.
 */
function setMeta(key: "name" | "property", value: string, content: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${value}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(key, value);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}
